import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Stamp } from "@/components/fronk/Stamp";
import { SceneVideo } from "@/components/fronk/SceneVideo";
import { STONKFUN_URL, shortRewardMint } from "@/lib/fronk";

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const STATS = [
  { value: "4%", label: "Transfer tax collected on every FRONK transfer" },
  { value: "TOAD", label: "Paid pro rata to qualifying FRONK holders" },
  { value: "$20", label: "Minimum desk to qualify for a run" },
];

const LEDGER = [
  { entry: "Payroll withholding", amount: "4 percent on every FRONK transfer" },
  { entry: "Routed to holders", amount: "Pro rata and paid in TOAD" },
  { entry: "Network and operations", amount: "2.50 percent, taken before distribution" },
  { entry: "Payout schedule", amount: "Distributions accumulate until a run makes sense" },
  { entry: "Desk minimum", amount: "20 dollars of FRONK held at distribution time" },
  { entry: "Reward mint", amount: shortRewardMint },
];

export const FeePipeline = () => {
  return (
    <section
      id="tubes"
      className="navy-grain relative overflow-hidden bg-[color:var(--ink-900)] py-16 sm:py-20 lg:py-28"
      data-testid="fee-pipeline-section"
    >
      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <Stamp>Department of tubes</Stamp>
          <h2 className="font-heading mt-5 text-3xl font-bold leading-tight text-[color:var(--paper-0)] sm:text-4xl lg:text-5xl">
            FEES IN. TOAD OUT.
          </h2>
          <p className="font-body mt-4 max-w-xl text-base leading-relaxed text-[color:var(--paper-1)]">
            Every $FRONK transfer feeds a 4 percent payroll withholding. The
            Pepe clerks route it pro rata to qualifying desks in TOAD, less the 2.50
            percent distribution and operations fee recorded by the contract.
          </p>
        </motion.div>

        <motion.div {...fadeUp} className="mt-10">
          <SceneVideo
            src="/fronk/motion/pepe-fee-pipeline-loop.mp4"
            poster="/fronk/scenes/pepe-fee-pipeline.webp"
            posterMobile="/fronk/scenes/pepe-fee-pipeline-mobile.webp"
            className="ink-frame aspect-[4/5] rounded-2xl sm:aspect-[1942/809]"
            alt="Pepe employees moving TOAD reward coins through a giant pneumatic tube system"
            testId="fee-pipeline-video"
          />
          <p className="font-mono-ledger mt-3 text-[10px] uppercase tracking-[0.2em] text-[color:var(--brass-200)]">
            Fig. 1. The fee pipeline, third shift, running at capacity.
            Reward mint {shortRewardMint}.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {STATS.map((s, i) => (
            <motion.div
              key={s.value}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.5,
                delay: i * 0.12,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="paper-grain relative overflow-hidden rounded-2xl bg-[color:var(--paper-0)] p-6 shadow-[var(--paper-lift)]"
              data-testid={`fee-stat-card-${i}`}
            >
              <div className="relative z-10">
                <div className="font-heading text-5xl font-bold text-[color:var(--ink-900)]">
                  {s.value}
                </div>
                <p className="font-mono-ledger mt-3 text-[11px] uppercase leading-relaxed tracking-[0.14em] text-[color:var(--ink-700)]">
                  {s.label}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div {...fadeUp} className="mt-10">
          <div className="paper-grain relative overflow-hidden rounded-2xl bg-[color:var(--paper-0)] shadow-[var(--paper-lift)]">
            <div className="relative z-10 p-2 sm:p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-2 pt-4">
                <span className="font-mono-ledger text-[11px] uppercase tracking-[0.2em] text-[color:var(--ink-700)]">
                  Ledger of record
                </span>
                <img
                  src="/fronk/props/old-calculator.webp"
                  alt=""
                  className="w-10 -rotate-6"
                />
              </div>
              <Table data-testid="tokenomics-ledger-table">
                <TableHeader>
                  <TableRow className="border-[color:var(--hairline)] hover:bg-transparent">
                    <TableHead className="font-mono-ledger text-[10px] uppercase tracking-[0.18em] text-[color:var(--ink-700)]">
                      Entry
                    </TableHead>
                    <TableHead className="font-mono-ledger text-right text-[10px] uppercase tracking-[0.18em] text-[color:var(--ink-700)]">
                      Amount
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {LEDGER.map((row) => (
                    <TableRow
                      key={row.entry}
                      className="border-[color:var(--hairline)] hover:bg-[color:var(--paper-1)]"
                    >
                      <TableCell className="font-body py-4 text-sm font-medium text-[color:var(--ink-900)]">
                        {row.entry}
                      </TableCell>
                      <TableCell className="font-mono-ledger py-4 text-right text-xs text-[color:var(--ink-800)] sm:text-sm">
                        {row.amount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <a
            href={STONKFUN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono-ledger mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-[color:var(--brass-200)] underline decoration-[color:var(--stamp-500)] underline-offset-4 transition-colors hover:text-[color:var(--paper-0)]"
            data-testid="tokenomics-source-link"
          >
            Read the full mechanics on the official StonkFun contract page
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
